import type { HTMLAttributes, MouseEvent, ReactElement } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Skeleton as DefaultSkeleton, Loader } from '@elfsight/widget-ui';
import { useLoadImage } from '@elfsight/widget-utils';
import type { MediaContainerProps } from '../container';
import { MediaContainer } from '../container';

export type MediaImageProps = Omit<MediaContainerProps, 'width' | 'height'> &
  HTMLAttributes<HTMLDivElement> & {
    src?: string;
    srcSet?: string;
    alt?: string;
    mode?: 'contain' | 'cover' | 'scale-down' | 'none';
    awaiting?: boolean;
    containerWidth?: string;
    containerHeight?: string;
    withLoader?: boolean;
    withSkeleton?: boolean;
    skeleton?: ReactElement;
    loaderSize?: number;
    loaderColor?: string;
    skeletonColor?: string;
    skeletonBorderRadius?: number | string;
    onClick?(event: MouseEvent): void;
    onLoad?(
      event?: Event,
      size?: {
        width?: number;
        height?: number;
      }
    ): void;
    onError?(): void;
  };

export const MediaImage = forwardRef<HTMLDivElement, MediaImageProps>(
  (
    {
      src,
      srcSet,
      alt,
      awaiting = false,
      mode = 'cover',
      containerWidth,
      containerHeight,
      withLoader = false,
      withSkeleton = false,
      skeleton,
      loaderSize = 32,
      loaderColor = '#212121',
      skeletonBorderRadius = 4,
      skeletonColor,
      onClick,
      onLoad,
      onError,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const { status } = useLoadImage(src, onLoad, onError);

    if (!src) {
      return null;
    }

    const finalSkeleton = skeleton || (
      <DefaultSkeleton
        width="100%"
        height="100%"
        borderRadius={skeletonBorderRadius}
        color={skeletonColor}
      />
    );

    return (
      <MediaContainer
        {...forwardingProps}
        width={containerWidth}
        height={containerHeight}
      >
        <Container ref={forwardingRef} _mode={mode} onClick={onClick}>
          {awaiting ? (
            finalSkeleton
          ) : (
            <>
              {status === 'loaded' && (
                <img src={src} alt={alt} srcSet={srcSet} />
              )}
              {status === 'pending' && (
                <>
                  {withLoader && (
                    <OverlayLoader size={loaderSize} color={loaderColor} />
                  )}
                  {withSkeleton && finalSkeleton}
                </>
              )}
            </>
          )}
        </Container>
      </MediaContainer>
    );
  }
);

const Container = styled.div<{
  _mode: string;
}>`
  ${({ _mode }) => css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    position: relative;

    & img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: ${_mode};
    }
  `}
`;

const OverlayLoader = styled(Loader)`
  position: absolute;
  inset: 0;
`;
